import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { fmtPrice } from "@/lib/currency";
import { Radar, CheckCircle2, ArrowRight } from "lucide-react";

export function GuestWantForm({ cmp, compact = false, initialText = "" }) {
  const { user } = useAuth();
  const [text, setText] = useState(initialText);
  const [maxPrice, setMaxPrice] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  if (user) {
    return (
      <Link to="/wanted" data-testid="guest-want-logged-in" className="inline-flex items-center gap-2 text-sm text-[#007AFF] hover:underline">
        <Radar className="h-4 w-4" /> Post what you want — sellers get alerted <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/match/demands/guest", {
        text: text.trim(), max_price: maxPrice ? Number(maxPrice) : null, email: email.trim(), cmp: cmp || null,
      });
      setDone(data);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || "Could not post your want — try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-xl border border-[#34C759]/40 bg-[#34C759]/[0.06] p-5" data-testid="guest-want-success">
        <div className="flex items-center gap-2 text-[#34C759] font-semibold"><CheckCircle2 className="h-4 w-4" /> Your want is live</div>
        <p className="mt-1 text-sm text-neutral-300">
          {done.matches.length > 0 ? `${done.matches.length} matching listing${done.matches.length === 1 ? "" : "s"} already — ` : "No exact match yet — "}
          we emailed <span className="font-mono text-white">{done.claim_sent_to}</span> a link to claim it and get alerts the moment a seller lists it.
        </p>
        {done.matches.length > 0 && (
          <ul className="mt-3 space-y-1.5" data-testid="guest-want-matches">
            {done.matches.slice(0, 3).map((m) => (
              <li key={m.id}>
                <Link to={`/product/${m.id}`} className="text-sm text-white hover:text-[#007AFF]">{m.title}</Link>
                <span className="ml-2 font-mono text-xs text-[#34C759]">{fmtPrice(m.price, m.currency)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`rounded-xl border border-[#27272A] bg-[#121212] ${compact ? "p-4" : "p-5"}`} data-testid="guest-want-form">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#FF3B30]"><Radar className="h-3.5 w-3.5" /> Tell sellers what you want</div>
      <p className="mt-1 text-sm text-neutral-400">No account needed. Sellers who have it get alerted; you get an email the moment it's listed.</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_120px]">
        <Input required minLength={3} maxLength={200} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Samsung A54 128GB, new, Lagos delivery" data-testid="guest-want-text" className="bg-black/40 border-white/10" />
        <Input type="number" min="0" step="0.01" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Max $" data-testid="guest-want-price" className="bg-black/40 border-white/10" />
      </div>
      <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto]">
        <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email for match alerts" data-testid="guest-want-email" className="bg-black/40 border-white/10" />
        <Button type="submit" disabled={busy} data-testid="guest-want-submit" className="rounded-full bg-[#FF3B30] hover:bg-[#e0352b] text-white font-semibold px-5">
          {busy ? "Posting…" : "Post it — free"}
        </Button>
      </div>
      {err && <div className="mt-2 text-xs text-[#FF3B30]" data-testid="guest-want-error">{err}</div>}
    </form>
  );
}
